import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetWriteOffCandidatesQuery } from '../../../../../../services/AccountService';
import { AccountView } from '../../../../../../services/AccountService/types/accountTypes';
import { WriteOffCandidatesRequest } from '../../../../../../services/AccountService/requests/writeOffCandidatesRequest';
import { useDateRangeFilter } from './useDateRangeFilter';
import { usePayerGroupFilter } from './usePayerGroupFilter';

export interface ProcessedAccount extends AccountView {
  ageDays: number;
}

export interface UseWriteOffCandidatesDataReturn {
  /** Raw candidates from API */
  candidates: AccountView[];
  /** Candidates with calculated ageDays */
  processedData: ProcessedAccount[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: any;
  /** Current center ID */
  centerId: string;
  /** Date filter hook return */
  dateFilter: ReturnType<typeof useDateRangeFilter>;
  /** Payer group filter hook return */
  payerGroupFilter: ReturnType<typeof usePayerGroupFilter>;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** The filters sent to the API, for consumers that must query the same set (e.g. CSV export) */
  queryParams: WriteOffCandidatesRequest;
}

/**
 * Hook that manages data fetching, filtering, and processing for WriteOff candidates.
 * Encapsulates all data-related logic for the grid.
 */
export function useWriteOffCandidatesData(): UseWriteOffCandidatesDataReturn {
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('center') || '';

  // Filter hooks
  const dateFilter = useDateRangeFilter({ storageKey: `writeoff-${centerId}` });
  const payerGroupFilter = usePayerGroupFilter({ storageKey: `writeoff-payers-${centerId}` });

  // Build query parameters
  const queryParams = useMemo(() => ({
    centerId,
    minimumAgeDays: 90,
    ...(dateFilter.isValid && dateFilter.value.from && { serviceDateFrom: dateFilter.value.from }),
    ...(dateFilter.isValid && dateFilter.value.to && { serviceDateTo: dateFilter.value.to }),
    ...(payerGroupFilter.value.length > 0 && { payerGroups: payerGroupFilter.value }),
  }), [centerId, dateFilter.isValid, dateFilter.value, payerGroupFilter.value]);

  // Fetch data
  const { data: candidates = [], isLoading, error } = useGetWriteOffCandidatesQuery(
    queryParams,
    { skip: !centerId }
  );

  // Process data - calculate age in days
  const processedData = useMemo(() => {
    return candidates.map((account): ProcessedAccount => {
      const serviceDate = new Date(account.encounterInfo?.serviceDate || new Date().toISOString());
      const today = new Date();
      const ageDays = Math.floor((today.getTime() - serviceDate.getTime()) / (1000 * 60 * 60 * 24));
      return { ...account, ageDays };
    });
  }, [candidates]);

  const hasActiveFilters = dateFilter.hasFilter || payerGroupFilter.hasFilter;

  return {
    candidates,
    processedData,
    isLoading,
    error,
    centerId,
    dateFilter,
    payerGroupFilter,
    hasActiveFilters,
    queryParams,
  };
}
